(() => {
  if (window.sleepyAddressAutocomplete) return;
  window.sleepyAddressAutocomplete = true;
  const key = document.querySelector('[data-scr-google-key]')?.dataset.scrGoogleKey;
  if (!key) return;
  let loading;
  const places = () => {
    if (window.google?.maps?.importLibrary) return google.maps.importLibrary('places');
    if (!loading) loading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timer = setTimeout(() => reject(new Error('timeout')), 15000);
      window.sleepyCollectionMapsReady = () => {
        clearTimeout(timer);
        google.maps.importLibrary('places').then(resolve, reject);
      };
      script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({key, loading:'async', libraries:'places', v:'weekly', language:'en', region:'NZ', callback:'sleepyCollectionMapsReady'})}`;
      script.async = true;
      script.onerror = () => { clearTimeout(timer); reject(new Error('network')); };
      document.head.append(script);
    });
    return loading;
  };
  const extract = components => {
    const value = type => components.find(part => part.types.includes(type))?.longText || '';
    const unit = value('subpremise');
    const street = [value('street_number'), value('route')].filter(Boolean).join(' ');
    return {
      street: unit && street ? `${unit}/${street}` : street,
      locality: value('sublocality_level_1') || value('sublocality') || value('locality'),
      postcode: value('postal_code'),
      country: components.find(part => part.types.includes('country'))?.shortText || ''
    };
  };
  let count = 0;
  const init = container => {
    if (container.dataset.autocompleteReady) return;
    container.dataset.autocompleteReady = 'true';
    const input = container.querySelector('[data-removal-street]');
    const locality = container.querySelector('[data-removal-locality]');
    const postcode = container.querySelector('[data-removal-postcode]');
    if (!input || !locality || !postcode) return;
    const box = document.createElement('div');
    box.className = 'scr-address-suggestions';
    box.hidden = true;
    const list = document.createElement('div');
    list.id = `scr-address-options-${++count}`;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Suggested collection addresses');
    const credit = document.createElement('div');
    credit.className = 'scr-address-credit';
    const logo = document.createElement('img');
    logo.src = 'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png';
    logo.alt = 'Powered by Google';
    logo.width = 120;
    logo.height = 14;
    credit.append(logo);
    box.append(list, credit);
    const help = document.createElement('p');
    help.id = `scr-address-help-${count}`;
    help.className = 'scr-address-help';
    help.setAttribute('role', 'status');
    help.setAttribute('aria-live', 'polite');
    help.textContent = 'Start typing to search New Zealand addresses with Google. You can also enter or edit all address fields manually.';
    const label = input.closest('label');
    label.after(box, help);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-describedby', help.id);
    input.setAttribute('autocomplete', 'off');
    let timer, revision = 0, session, suggestions = [], active = -1;
    let press = null, selecting = false, clickBlockedUntil = 0;
    const close = () => {
      box.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    };
    const highlight = index => {
      active = index;
      [...list.children].forEach((option, position) => option.setAttribute('aria-selected', String(index === position)));
      if (index >= 0) {
        input.setAttribute('aria-activedescendant', list.children[index].id);
        list.children[index].scrollIntoView({block:'nearest'});
      }
    };
    const select = async index => {
      const prediction = suggestions[index];
      if (!prediction || selecting) return;
      selecting = true;
      const current = ++revision;
      const before = [input.value, locality.value, postcode.value];
      clearTimeout(timer);
      close();
      help.textContent = 'Loading the selected address…';
      try {
        const place = prediction.toPlace();
        await place.fetchFields({fields:['addressComponents']});
        if (current !== revision || before.some((value, i) => value !== [input.value, locality.value, postcode.value][i])) return;
        const address = extract(place.addressComponents || []);
        if (address.country !== 'NZ' || !address.street) throw new Error('incomplete');
        input.value = address.street;
        locality.value = address.locality;
        postcode.value = /^\d{4}$/.test(address.postcode) ? address.postcode : '';
        for (const field of [input, locality, postcode]) {
          field.setCustomValidity('');
          field.dispatchEvent(new Event('input', {bubbles:true}));
          field.dispatchEvent(new Event('change', {bubbles:true}));
        }
        clearTimeout(timer);
        close();
        help.textContent = address.locality && postcode.value
          ? 'Address filled. Please check the details, add any unit number, then check your collection price. You can edit all three fields.'
          : 'Please complete the missing suburb or postcode, check the street and unit number, then check your collection price.';
      } catch {
        if (current === revision) help.textContent = 'We could not fill this address. Please enter the street, suburb and postcode manually.';
      } finally { selecting = false; session = undefined; }
    };
    const search = async (query, current) => {
      help.textContent = 'Searching addresses…';
      try {
        const library = await places();
        if (current !== revision || !input.isConnected || document.activeElement !== input) return;
        session ||= new library.AutocompleteSessionToken();
        const result = await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query, includedRegionCodes:['nz'], language:'en', region:'nz',
          locationBias:{north:-35.7,south:-37.6,east:175.7,west:174.0}, sessionToken:session
        });
        if (current !== revision || document.activeElement !== input) return;
        suggestions = result.suggestions.map(item => item.placePrediction).filter(Boolean);
        list.replaceChildren();
        suggestions.forEach((prediction, index) => {
          const option = document.createElement('button');
          option.type = 'button';
          option.tabIndex = -1;
          option.id = `${list.id}-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.textContent = prediction.text.toString();
          option.addEventListener('pointerdown', event => {
            if (event.isPrimary === false || event.button > 0) return;
            press = {id:event.pointerId, index, x:event.clientX, y:event.clientY, moved:false};
            if (event.pointerType === 'mouse') event.preventDefault();
          });
          option.addEventListener('pointermove', event => {
            if (press?.id !== event.pointerId) return;
            if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) press.moved = true;
          });
          option.addEventListener('pointerup', event => {
            if (press?.id !== event.pointerId) return;
            const moved = press.moved;
            press = null;
            clickBlockedUntil = Date.now() + 600;
            if (!moved) { event.preventDefault(); select(index); }
            else if (document.activeElement !== input) { ++revision; close(); }
          });
          option.addEventListener('click', () => {
            if (Date.now() >= clickBlockedUntil) select(index);
          });
          list.append(option);
        });
        box.hidden = !suggestions.length;
        input.setAttribute('aria-expanded', String(!!suggestions.length));
        help.textContent = suggestions.length ? 'Choose your full address. Use the arrow keys and Enter, or tap a suggestion.' : 'No matching address found. Please enter the street, suburb and postcode manually.';
      } catch {
        if (current !== revision) return;
        close();
        help.textContent = 'Address suggestions are unavailable. Please enter the street, suburb and postcode manually.';
      }
    };
    input.addEventListener('input', event => {
      if (!event.isTrusted) return;
      clearTimeout(timer);
      const current = ++revision;
      close();
      const query = input.value.trim();
      if (query.length < 2) return;
      timer = setTimeout(() => search(query, current), 350);
    });
    for (const field of [locality, postcode]) field.addEventListener('input', event => {
      if (event.isTrusted) { ++revision; clearTimeout(timer); close(); }
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        ++revision;
        press = null;
        clickBlockedUntil = Date.now() + 600;
        clearTimeout(timer);
        close();
      }
      if (box.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        highlight((active + (event.key === 'ArrowDown' ? 1 : suggestions.length - 1) + suggestions.length) % suggestions.length);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (active >= 0) select(active);
      } else if (event.key === 'Tab') close();
    });
    input.addEventListener('blur', () => {
      clearTimeout(timer);
      if (press || selecting) return;
      ++revision;
      close();
    });
    const cancelPress = () => {
      if (!press) return;
      press = null;
      clickBlockedUntil = Date.now() + 600;
      if (document.activeElement !== input && !selecting) { ++revision; close(); }
    };
    document.addEventListener('pointerup', cancelPress);
    document.addEventListener('pointercancel', cancelPress);
    container.addEventListener('focusout', event => { if (!container.contains(event.relatedTarget)) session = undefined; });
  };
  const scan = () => document.querySelectorAll('[data-removal-address]').forEach(init);
  scan();
  new MutationObserver(scan).observe(document.body, {childList:true, subtree:true});
})();
